import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type {
  ShiftFormActor,
  ApprovalStage,
  ShiftProductionForm,
} from "../../../shared/shift-production-form/contracts";
import { approvalFormPath } from "../../../shared/shift-production-form/approval-routes";
import { SystemState } from "../../shared/components/SystemState";
import { Toast } from "../../shared/components/Toast";
import { useToastNotification } from "../../shared/components/useToastNotification";
import { ShiftFormEditor } from "../shift-production-form/ShiftFormEditor";
import { useShiftProductionFormWorkspace } from "../shift-production-form/useShiftProductionFormWorkspace";
import { AuthScreen } from "../auth/AuthScreen";
import {
  useShiftFormApprovalWorkspace,
} from "./useShiftFormApprovalWorkspace";
import { approvalRevisionDiff } from "./approval-revision-diff";
import { approvalRevisionView } from "./approval-revision-view";
import { approvalAccessState } from "./approval-access-state";
import { TodayApprovedForms } from "./TodayApprovedForms";
import { useTodayApprovedForms } from "./useTodayApprovedForms";
import { ApprovalQueuePanel } from "./ApprovalQueuePanel";
import { ApprovalDetailDialogMarkup } from "./ApprovalDetailDialogMarkup";
import { ApproverPinLogin } from "./ApproverPinLogin";
import "./approval-queue-panel.css";
import "./shift-form-approval.css";

const stageLabel = {
  SHIFT_SUPERVISOR: "Vardiya amiri",
  PRODUCTION_ENGINEER: "Üretim şefi",
  PRODUCTION_MANAGER: "Üretim müdürü",
} as const;

function documentSettingsForApproval(form: ShiftProductionForm) {
  return {
    documentNo: form.payload.documentNo,
    publicationDate: form.payload.publicationDate,
    revisionDate: form.payload.revisionDate,
    revisionNo: form.payload.revisionNo,
    version: form.version,
    updatedAt: form.updatedAt,
    updatedBy: null,
  };
}

function ApprovalReview({
  form,
  comparisonPayload = null,
  busy,
  feedback,
  feedbackTone,
  onDecide,
}: {
  form: ShiftProductionForm;
  comparisonPayload?: ShiftProductionForm["payload"] | null;
  busy: boolean;
  feedback: string | null;
  feedbackTone: "error" | "success";
  onDecide: (
    action: "APPROVE",
    explanation: string,
    missingMeasurementAcceptanceReason: string,
  ) => Promise<void>;
}) {
  const [missingReason, setMissingReason] = useState("");
  const canApproveMissing = !form.hasMissingMeasurements || missingReason.trim().length > 0;
  const revisionDiff = comparisonPayload
    ? approvalRevisionDiff(form.payload, comparisonPayload)
    : null;

  useEffect(() => {
    setMissingReason("");
  }, [form.id, form.version]);

  return (
    <div className="shift-approval-review">
      <div className="shift-approval-review-body">
        <section className="shift-approval-readonly" aria-label="Form içeriği">
          <ShiftFormEditor
            key={form.id}
            readOnly
            readOnlyRevisionDiff={revisionDiff}
            actorName={form.payload.operatorName}
            documentSettings={documentSettingsForApproval(form)}
            lineUnits={[]}
            lineUnitsError={null}
            onRetryLineUnits={() => undefined}
            form={form}
            selectionVersion={form.version}
            busy={busy}
            error={null}
            notice={null}
            onSave={async () => null}
            onNew={() => undefined}
          />
        </section>
      </div>
      {form.canDecide || feedback ? (
        <footer className="shift-approval-decision-footer" aria-label="Onay işlemleri">
          {feedback ? <p className={`shift-approval-decision-feedback is-${feedbackTone}`} role={feedbackTone === "error" ? "alert" : "status"}>{feedback}</p> : null}
          {form.canDecide && form.hasMissingMeasurements ? (
            <label className="shift-approval-return">
              <span>Eksik ölçümü kabul gerekçesi</span>
              <textarea
                value={missingReason}
                onChange={(event) => setMissingReason(event.target.value)}
                placeholder="Eksik ölçümün neden kabul edildiğini yazın"
              />
            </label>
          ) : null}
          {form.canDecide ? <div className="shift-approval-review-actions">
            <button
              type="button"
              className="is-primary"
              disabled={busy || !canApproveMissing}
              onClick={() => void onDecide("APPROVE", "", missingReason)}
            >
              Onayla ve sıradakine geç
            </button>
          </div> : null}
        </footer>
      ) : null}
    </div>
  );
}

function ApprovalDetailDialog({
  form,
  readOnly,
  busy,
  feedback,
  feedbackTone,
  onDecide,
  onClose,
}: {
  form: ShiftProductionForm;
  readOnly: boolean;
  busy: boolean;
  feedback: string | null;
  feedbackTone: "error" | "success";
  onDecide: (
    action: "APPROVE",
    explanation: string,
    missingMeasurementAcceptanceReason: string,
  ) => Promise<void>;
  onClose: () => void;
}) {
  const [revisionSelection, setRevisionSelection] = useState<{ formId: string; number: number } | null>(null);
  const revisionView = approvalRevisionView(form, revisionSelection?.formId === form.id ? revisionSelection.number : null, readOnly);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.showModal();
    closeButtonRef.current?.focus({ preventScroll: true });
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected && !previousFocus.closest("[inert]"))
        previousFocus.focus({ preventScroll: true });
    };
  }, []);

  useEffect(() => {
    const body = dialogRef.current?.querySelector<HTMLElement>(".shift-approval-dialog-body");
    body?.scrollTo({ top: 0, behavior: "instant" });
    closeButtonRef.current?.focus({ preventScroll: true });
  }, [form.id]);

  return createPortal(
    <ApprovalDetailDialogMarkup
      formNo={form.formNo}
      departmentName={revisionView.form.payload.departmentName}
      lineName={revisionView.form.payload.lineName}
      busy={busy}
      revisionControl={revisionView.numbers.length > 1 ? (
        <label className="shift-approval-revision-picker">
          <span className="sr-only">Form sürümü</span>
          <select aria-label="Form sürümü" disabled={busy} value={revisionView.selected} onChange={(event) => {
            setRevisionSelection({ formId: form.id, number: Number(event.target.value) });
            dialogRef.current?.querySelector<HTMLElement>(".shift-approval-dialog-body")?.scrollTo({ top: 0 });
          }}>
            {revisionView.numbers.map(number => <option key={number} value={number}>Sürüm {number}{number === form.revisionNumber ? " · Güncel" : ""}</option>)}
          </select>
        </label>
      ) : null}
      dialogRef={dialogRef}
      closeButtonRef={closeButtonRef}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) closeRef.current();
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) closeRef.current();
      }}
      onKeyDown={(event) => {
        if (event.key !== "Tab") return;
        const dialogElement = event.currentTarget;
        const focusable = Array.from(
          dialogElement.querySelectorAll<HTMLElement>(
            "button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, a[href], [tabindex]:not([tabindex='-1'])",
          ),
        ).filter((item) => item.getClientRects().length > 0);
        const first = focusable[0];
        const last = focusable.at(-1);
        if (!first || !last) {
          event.preventDefault();
          closeButtonRef.current?.focus();
        } else if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }}
    >
      <ApprovalReview key={`${form.id}:${revisionView.selected}`} form={revisionView.form} comparisonPayload={revisionView.historical ? form.payload : null} busy={busy} feedback={feedback} feedbackTone={feedbackTone} onDecide={async (...args) => {
        if (!readOnly && !revisionView.historical) await onDecide(...args);
      }} />
    </ApprovalDetailDialogMarkup>,
    document.body,
  );
}

function ApprovalWorkspace({ actor, initialFormId, initialStage, routeStage, redirectToCurrentStage, onLogout }: { actor: ShiftFormActor; initialFormId: string | null; initialStage: ApprovalStage; routeStage: ApprovalStage | null; redirectToCurrentStage: boolean; onLogout?: () => void }) {
  const workspace = useShiftFormApprovalWorkspace(initialFormId, initialStage);
  const approved = useTodayApprovedForms(initialStage, actor.id, workspace.approvalRefreshVersion);
  const toast = useToastNotification(workspace.error ?? workspace.notice, workspace.error ? "error" : "success");
  const deepLinkRedirectHandled = useRef(false);

  useEffect(() => {
    const currentStage = workspace.selected?.currentApprovalStage;
    if (deepLinkRedirectHandled.current || !initialFormId || !workspace.selected) return;
    deepLinkRedirectHandled.current = true;
    if (!currentStage || (!redirectToCurrentStage && routeStage === currentStage)) return;
    window.location.replace(approvalFormPath(currentStage, workspace.selected?.id));
  }, [initialFormId, redirectToCurrentStage, routeStage, workspace.selected?.currentApprovalStage, workspace.selected?.id]);

  return (
    <main className="shift-approval-workspace">
      <h1 className="sr-only">{stageLabel[initialStage]} onayları</h1>
      <ApprovalQueuePanel
        title="Onay bekleyen formlar"
        actorName={actor.name}
        items={workspace.items}
        total={workspace.total}
        busy={workspace.busy}
        phase={workspace.phase}
        error={workspace.error}
        onOpen={(id) => void workspace.open(id)}
        onReload={() => void workspace.reload()}
        onLogout={onLogout}
      />
      <TodayApprovedForms {...approved} busy={workspace.busy} onOpen={id => void workspace.open(id, true)} onReload={() => void approved.reload()} />
      {workspace.selected ? <ApprovalDetailDialog readOnly={workspace.selectedReadOnly} form={workspace.selected} busy={workspace.busy} feedback={workspace.error ?? workspace.notice} feedbackTone={workspace.error ? "error" : "success"} onDecide={workspace.decide} onClose={workspace.closeForm} /> : null}
      {toast.notification ? <Toast message={toast.notification.message} tone={toast.notification.tone} onDismiss={toast.dismiss} /> : null}
    </main>
  );
}

export function ShiftFormApprovalModule({
  initialFormId = null,
  routeStage = null,
  redirectToCurrentStage = false,
}: {
  initialFormId?: string | null;
  routeStage?: ApprovalStage | null;
  redirectToCurrentStage?: boolean;
}) {
  const sessionWorkspace = useShiftProductionFormWorkspace();
  const actor = sessionWorkspace.session.phase === "ready" ? sessionWorkspace.session.actor : null;
  const accessState = approvalAccessState(sessionWorkspace.session.phase, actor);
  const operatorSessionClearStarted = useRef(false);

  useEffect(() => {
    if (actor?.kind !== "OPERATOR") {
      operatorSessionClearStarted.current = false;
      return;
    }
    if (operatorSessionClearStarted.current) return;
    operatorSessionClearStarted.current = true;
    void sessionWorkspace.clearOperatorSession().catch(() => undefined);
  }, [actor?.kind, sessionWorkspace.clearOperatorSession]);

  if (accessState === "LOADING") return <SystemState mode="page" tone="loading" title="Onay ekranı hazırlanıyor" description="Kurumsal oturumunuz doğrulanıyor." />;
  if (accessState === "SERVICE_UNAVAILABLE") return <AuthScreen mode="service-unavailable" onRetry={sessionWorkspace.reload} />;
  if (accessState === "CLEAR_OPERATOR") return <SystemState mode="page" tone="loading" title="Kurumsal oturum hazırlanıyor" description="Operatör oturumu güvenli biçimde kapatılıyor." />;
  if (accessState === "AUTH") return <ApproverPinLogin workspace={sessionWorkspace} />;
  if (!actor) return <ApproverPinLogin workspace={sessionWorkspace} />;
  return <ApprovalWorkspace actor={actor} initialFormId={initialFormId} initialStage={routeStage ?? "SHIFT_SUPERVISOR"} routeStage={routeStage} redirectToCurrentStage={redirectToCurrentStage} onLogout={actor.kind === "APPROVER" ? () => void sessionWorkspace.logout() : undefined} />;
}
